import { useSearchParams } from "react-router-dom";
import { Topbar } from "@marekl/adminer/shell";
import {
  Page,
  Table,
  type Column,
  Input,
  Select,
  FilterField,
  FilterPanel,
  DatePicker,
  LoadingState,
  ErrorState,
  EmptyState,
  Pagination,
  Text,
  DateTimeField,
} from "@marekl/adminer/components";
import {
  useAuditLogs,
  useAuditLogUsers,
} from "../../lib/queries/auditLogs";
import type { AuditLogEntry } from "../../lib/queries/auditLogs";
import { usePaginatedQuery, useDebouncedSearchFilter } from "@marekl/adminer/hooks";
import { dayjs, toApiDate, type Dayjs } from "@marekl/adminer/datetime";
import { labelAction, labelEntityType, ENTITY_TYPE_OPTIONS } from "./auditLabels";
import styles from "./AuditLogPage.module.css";

const LIMIT_OPTIONS = [10, 25, 50, 100];
const DEFAULT_LIMIT = 50;

export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const userIdParam = searchParams.get("user_id") ?? "";
  const entityType = searchParams.get("entity_type") ?? "";
  const q = searchParams.get("q") ?? "";

  const fromDate: Dayjs | null = from ? dayjs(from, "YYYY-MM-DD", true) : null;
  const toDate: Dayjs | null = to ? dayjs(to, "YYYY-MM-DD", true) : null;

  const { data, isLoading, isError, pagination, filterReset } =
    usePaginatedQuery(
      useAuditLogs,
      {
        from: from || undefined,
        to: to || undefined,
        user_id: userIdParam ? Number(userIdParam) : undefined,
        entity_type: entityType || undefined,
        q: q || undefined,
      },
      {
        searchParams,
        setSearchParams,
        defaultLimit: DEFAULT_LIMIT,
        limitOptions: LIMIT_OPTIONS,
      },
    );
  const { data: users = [] } = useAuditLogUsers();

  function setFilter(key: string, value: string) {
    filterReset((p) => {
      if (value) {
        p.set(key, value);
      } else {
        p.delete(key);
      }
    });
  }

  const {
    draft: qDraft,
    setDraft: setQDraft,
  } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => setFilter("q", next),
  });

  function setDateFilter(key: string, value: Dayjs | null) {
    const s = toApiDate(value);
    setFilter(key, s ?? "");
  }

  const columns: Column<AuditLogEntry>[] = [
    {
      key: "created_at",
      header: "Čas",
      render: (row) => (
        <Text variant="mono"><DateTimeField record={row} source="created_at" /></Text>
      ),
    },
    {
      key: "user",
      header: "Uživatel",
      render: (row) =>
        row.display_name || row.username || (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "action",
      header: "Akce",
      render: (row) => labelAction(row.action),
    },
    {
      key: "entity",
      header: "Entita",
      render: (row) =>
        row.entity_type ? (
          <span>
            {labelEntityType(row.entity_type)}
            {row.entity_id ? (
              <> <Text variant="mono">{row.entity_id}</Text></>
            ) : null}
          </span>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "details",
      header: "Detail",
      render: (row) =>
        row.details ? (
          <span className={styles.detailCell}>{row.details}</span>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
  ];

  const items = data?.items ?? [];
  const total = data?.total_estimate ?? 0;

  return (
    <>
      <Topbar crumbs={[{ label: "Audit log" }]} />
      <Page.Content>
        <Page.Header
          title="Audit log"
          subtitle="záznamy nejdou mazat — jen prohlížet a filtrovat"
        />

        <FilterPanel>
            <FilterField>
              <DatePicker
                label="Od"
                value={fromDate}
                onChange={(d) => setDateFilter("from", d)}
              />
            </FilterField>
            <FilterField>
              <DatePicker
                label="Do"
                value={toDate}
                onChange={(d) => setDateFilter("to", d)}
              />
            </FilterField>
            <FilterField>
              <Select
                label="Uživatel"
                value={userIdParam}
                onChange={(e) => setFilter("user_id", e.target.value)}
              >
                <option value="">Všichni</option>
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.display_name || u.username}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField>
              <Select
                label="Entita"
                value={entityType}
                onChange={(e) => setFilter("entity_type", e.target.value)}
              >
                {ENTITY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField wide>
              <Input
                label="ID / hledat"
                type="search"
                value={qDraft}
                placeholder="např. 8594000123456"
                onChange={(e) => setQDraft(e.target.value)}
              />
            </FilterField>
        </FilterPanel>

        {/* Results header */}
        <Page.SectionHeader
          title="Záznamy"
          meta={total > 0 ? `${items.length} z ${total}` : undefined}
        />

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error="Nepodařilo se načíst audit log." />
        ) : (
          <Table
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            empty={
              <EmptyState
                title="Žádné záznamy"
                description="Pro zadané filtry neexistují žádné záznamy."
              />
            }
          />
        )}

        {/* Pagination footer */}
        <Pagination
          {...pagination}
          summary={
            `Zobrazeno ${items.length}` +
            (total > 0 ? ` z ${total} záznamů` : " záznamů")
          }
        />
      </Page.Content>
    </>
  );
}
